/* THE SKY: what the hour does to the light.
 *
 * The relief pass in `display.ts` shades the frame from a normal buffer with
 * one key light and up to sixteen point lights, under a flat ambient. It
 * shipped fixed: the same key from up-left at every hour, so the world's
 * clock (`clockOf`, which already runs the tide, the fires' dryness and the
 * wisps' pull toward light) made no picture. This turns the clock into a sky:
 * keyframes over the day for the ambient floor, its colour, the key's
 * direction and strength, interpolated smoothly, plus `dark` -- how much of
 * the day is gone -- which the game uses to let a lit thing count for more as
 * evening comes.
 *
 * The floor never drops so far that the floor of the world is hidden: a
 * top-down game that conceals its own ground is a different game (see the
 * note on RELIEF_DEFAULT). Night is blue and dim and a brazier matters; it is
 * not black. */

export type Sky = {
  day: number;            // 0 dawn .. 0.5 dusk .. 1 dawn, as `clockOf` counts it
  ambient: number;        // the floor
  tint: [number, number, number];
  sun: [number, number, number];
  sunAmt: number;
  dark: number;           // 0 at noon, 1 at the dead of night
  relief: number;
};

/* Each keyframe: day, ambient, tint, sun direction (x right, y DOWN the
 * screen, z toward the viewer), key strength. The key's y is negative when
 * the sun stands high (light from the top of the screen); at dawn it lies
 * low in the east (+x), at dusk low in the west (-x). */
const KEYS: [number, number, [number, number, number], [number, number, number], number][] = [
  [0.00, 0.62, [1.00, 0.80, 0.70], [0.75, -0.30, 0.45], 0.36],   // dawn: low, warm, from the east
  [0.12, 0.82, [1.00, 0.95, 0.88], [0.30, -0.60, 0.60], 0.26],
  [0.30, 0.88, [1.00, 1.00, 1.00], [-0.45, -0.65, 0.62], 0.20],   // noon: the shipped key
  [0.45, 0.82, [1.00, 0.94, 0.84], [-0.60, -0.45, 0.55], 0.26],
  [0.55, 0.60, [1.00, 0.70, 0.52], [-0.80, -0.20, 0.42], 0.36],   // dusk: low, orange, from the west
  [0.64, 0.46, [0.62, 0.68, 0.98], [-0.20, -0.70, 0.60], 0.10],
  [0.78, 0.42, [0.55, 0.62, 0.96], [0.00, -0.75, 0.62], 0.08],    // the dead of night: blue, a moon overhead
  [0.92, 0.48, [0.66, 0.70, 0.96], [0.40, -0.55, 0.55], 0.12],
  [1.00, 0.62, [1.00, 0.80, 0.70], [0.75, -0.30, 0.45], 0.36],   // and dawn again
];

export const NOON_DAY = 0.30;
export const NIGHT_DAY = 0.78;

const smooth = (t: number) => t * t * (3 - 2 * t);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export function skyAt(day: number): Sky {
  const d = ((day % 1) + 1) % 1;
  let i = 0;
  while (i < KEYS.length - 2 && KEYS[i + 1][0] <= d) i++;
  const a = KEYS[i], b = KEYS[i + 1];
  const t = smooth((d - a[0]) / (b[0] - a[0]));
  const ambient = lerp(a[1], b[1], t);
  const tint: [number, number, number] = [lerp(a[2][0], b[2][0], t), lerp(a[2][1], b[2][1], t), lerp(a[2][2], b[2][2], t)];
  const sun: [number, number, number] = [lerp(a[3][0], b[3][0], t), lerp(a[3][1], b[3][1], t), lerp(a[3][2], b[3][2], t)];
  const sunAmt = lerp(a[4], b[4], t);
  const dark = Math.max(0, Math.min(1, (0.88 - ambient) / (0.88 - 0.42)));
  // Trade flat daylight fill for directional light without increasing the
  // maximum exposure. Keep the night floor and gameplay darkness unchanged.
  const shape = 0.12 * (1 - dark);
  return { day: d, ambient: ambient - shape, tint, sun, sunAmt: sunAmt + shape, dark, relief: 1 };
}
